import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';

type FulfillmentStatus = 'pending' | 'in_progress' | 'finished' | 'failed' | 'canceled';

const STATUS_CONTENT: Record<FulfillmentStatus, {
  titleKey: string;
  subtitleKey: string | null;
  statusTitleKey: string;
  statusTextKey: string;
  statusNoteKey: string | null;
  statusStyle: string;
  titleStyle: string;
}> = {
  pending: {
    titleKey: 'payment.received.title',
    subtitleKey: 'payment.received.pendingSubtitle',
    statusTitleKey: 'payment.queue.title',
    statusTextKey: 'payment.queue.desc',
    statusNoteKey: 'payment.queue.wait',
    statusStyle: 'border-[#818CF8] bg-[#EEF2FF] text-[#4338CA]',
    titleStyle: 'text-[#111827]',
  },
  in_progress: {
    titleKey: 'payment.received.title',
    subtitleKey: 'payment.received.inProgressSubtitle',
    statusTitleKey: 'payment.registering.title',
    statusTextKey: 'payment.registering.desc',
    statusNoteKey: 'payment.registering.wait',
    statusStyle: 'border-[#818CF8] bg-[#EEF2FF] text-[#4338CA]',
    titleStyle: 'text-[#111827]',
  },
  finished: {
    titleKey: 'success.title',
    subtitleKey: null,
    statusTitleKey: 'payment.success.title',
    statusTextKey: 'payment.success.desc',
    statusNoteKey: null,
    statusStyle: 'border-[#059669] bg-[#ECFDF5] text-[#059669]',
    titleStyle: 'text-[#111827]',
  },
  failed: {
    titleKey: 'payment.failed.title',
    subtitleKey: 'payment.failed.subtitle',
    statusTitleKey: 'payment.failed.title',
    statusTextKey: 'payment.failed.desc',
    statusNoteKey: null,
    statusStyle: 'border-[#EC0004] bg-[#FFEDED] text-[#EC0004]',
    titleStyle: 'text-[#111827]',
  },
  canceled: {
    titleKey: 'payment.canceled.title',
    subtitleKey: 'payment.canceled.subtitle',
    statusTitleKey: 'payment.canceled.statusTitle',
    statusTextKey: 'payment.canceled.statusText',
    statusNoteKey: null,
    statusStyle: 'border-[#EC0004] bg-[#FFEDED] text-[#EC0004]',
    titleStyle: 'text-[#111827]',
  },
};

export default function SuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const country = params.get('country') || '';
  const period = params.get('period') || '';
  const plate = params.get('plate') || '';
  const regCountry = params.get('reg_country') || '';
  const total = params.get('total') || '';
  const currency = params.get('currency') || 'EUR';
  const rawStatus = params.get('status') || 'pending';
  const status = (STATUS_CONTENT[rawStatus as FulfillmentStatus] ? rawStatus : 'pending') as FulfillmentStatus;

  const content = STATUS_CONTENT[status];
  const title = t(content.titleKey);
  const subtitle = status === 'finished' && email
    ? `${t('success.sentToBefore')} ${email}`
    : content.subtitleKey ? t(content.subtitleKey) : null;

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header />
      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="mx-auto w-full max-w-md text-center">
          <div className="mb-[24px] flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ECFDF5]">
              <i className="ri-check-double-line text-[32px] text-[#059669]" />
            </div>
          </div>

          <h2 className={`mb-3 text-[34px] font-bold tracking-tight ${content.titleStyle}`}>{title}</h2>

          {subtitle && <p className="text-[#374151]">{subtitle}</p>}

          {country && period && (
            <p className="text-[#6B7280] text-sm leading-[21px] my-[4px]">
              {country.toUpperCase()} — {period} {t('success.vignetteNoun')}{regCountry && plate ? ` — ${regCountry} ${plate}` : ''}
            </p>
          )}

          {total && (
            <p className="mb-8 text-[#059669] text-sm leading-[21px] font-semibold">
              {t('success.paid', { amount: `${total} ${currency}` })}
            </p>
          )}

          <div className={`text-left rounded-[18px] border mb-[6px] py-[16px] px-[25px] ${content.statusStyle}`}>
            <h3 className="text-sm font-semibold mb-[6px]">{t(content.statusTitleKey)}</h3>
            <p className="text-[12px]">{t(content.statusTextKey)}</p>
            {content.statusNoteKey && (
              <p className="text-[12px] font-semibold mt-[6px]">{t(content.statusNoteKey)}</p>
            )}
          </div>

          <div className="mb-6 rounded-xl border border-border bg-surface p-5 text-left text-[14px] text-muted">
            <p className="mb-[12px] font-semibold text-ink">{t('success.whatNext')}</p>
            <ul className="mt-2 space-y-1.5 list-none">
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-mail-line text-[11px]" />
                </span>
                {t('success.next1')}
              </li>
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-file-list-3-line text-[11px]" />
                </span>
                {t('success.next2')}
              </li>
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-road-map-line text-[11px]" />
                </span>
                {t('success.next3')}
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to={`/${country}`}
              className="inline-flex items-center text-white justify-center rounded-lg border border-border bg-primary px-6 py-3 text-[14px] font-medium transition hover:border-primary cursor-pointer"
            >
              {t('success.backHome')}
            </Link>
            <Link
              to="/#countries"
              className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-[14px] font-semibold text-black transition cursor-pointer border border-[#E5E7EB]"
            >
              {t('success.buyAnother')}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
